import queryString from 'query-string';

import {
  apiUrl,
  apiUrlV1,
  cancellableJsRequest,
  cancellableRequest,
} from 'src/services/api.js';
import { extractDate } from 'src/utils/datetime.js';
import { createObjectMapper } from 'src/utils/object.js';

const paramsMap = {
  type: 'type',
  query: 'q',
  order: 'sort',
  divisions: 'divisions',
  qualification: 'qid',
  skills: 'skills',
  salary: 'salary',
  currency: 'currency',
  location: 'locations',
  company: 'company_id',
  excludeCompany: 'exclude_company',
  employment: 'employment_type',
  remoteWork: 'remote',
  hasSalary: 'with_salary',
};

const mapLocationParams = createObjectMapper({
  filterEmpty: true,
  defaults: {
    order: 'relevance',
    currency: 'RUR',
  },
  map: paramsMap,
  remove: ['alias'],
});

const mapRSSParams = createObjectMapper({
  filterEmpty: true,
  map: paramsMap,
  remove: ['page'],
});

const mapRequestParams = createObjectMapper({
  filterEmpty: true,
  map: paramsMap,
});

const mapSuitableUsersParams = createObjectMapper({
  filterEmpty: true,
  remove: ['vacancyId', 'type'],
});

export const VacanciesService = {
  getList(params) {
    const { alias, ...rest } = params;
    let url = `${apiUrl}/vacancies`;
    if (alias) url += `/${alias}`;

    return cancellableRequest({
      method: 'get',
      url,
      params: mapRequestParams(rest),
    });
  },
  getMappedParams(params) {
    return mapLocationParams(params);
  },
  syncLocation(params) {
    const mappedParams = mapLocationParams(params);
    // todo: прикрутить сериализацию вложенных объектов
    const urlQuery = queryString.stringify(mappedParams, {
      arrayFormat: 'bracket',
    });
    window.history.replaceState(
      params,
      window.location.title,
      `/vacancies?${urlQuery}`,
    );
  },
  syncLocationPageOnly(page) {
    window.history.replaceState(
      null,
      window.location.title,
      `${window.location.pathname}?page=${page}`,
    );
  },
  getRssLink(params) {
    return `/vacancies/rss?${queryString.stringify(mapRSSParams(params), {
      arrayFormat: 'bracket',
    })}`;
  },
  getAddSeoLink(params) {
    return `/admin/seo_landing_filters/new?${queryString.stringify(
      mapRSSParams(params),
      {
        arrayFormat: 'bracket',
      },
    )}`;
  },
  getUpdateSeoFilter(alias, params) {
    return `/admin/seo_landings/update_filter?${queryString.stringify(
      mapRSSParams({ ...params, alias }),
      {
        arrayFormat: 'bracket',
      },
    )}`;
  },
};

export const VacanciesSubscriptionsResource = {
  save(params) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancy_subscriptions`,
      params: mapRequestParams(params),
    });
  },
  async hasSubscriptions() {
    const res = await cancellableRequest({
      method: 'get',
      url: `${apiUrlV1}/vacancy_subscriptions`,
    }).catch(() => null);

    return Boolean(res?.data?.vacancySubscriptions?.length);
  },
};

export const VacanciesResponsesResultResource = {
  saveTrackResult({ id, data }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrlV1}/vacancies/${id}/response_screen_result`,
      data,
    });
  },
};

export const VacanciesUserFiltersResource = {
  save(params) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/user_filters/vacancies`,
      params: mapRequestParams(params),
    });
  },
  delete({ id }) {
    return cancellableRequest({
      method: 'delete',
      url: `${apiUrl}/user_filters/vacancies/${id}`,
    });
  },
};

export const VacanciesResponsesResource = {
  all: {
    get({ vacancyId, page }) {
      return cancellableRequest({
        method: 'get',
        url: `${apiUrl}/vacancies/${vacancyId}/responses`,
        params: {
          page,
        },
      });
    },
  },
  favorited: {
    get({ vacancyId, page }) {
      return cancellableRequest({
        method: 'get',
        url: `${apiUrl}/vacancies/${vacancyId}/responses/favorited`,
        params: {
          page,
        },
      });
    },
  },
  archived: {
    get({ vacancyId, page }) {
      return cancellableRequest({
        method: 'get',
        url: `${apiUrl}/vacancies/${vacancyId}/responses/archived`,
        params: {
          page,
        },
      });
    },
  },
  post({ vacancyId, data }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/responses`,
      data,
    });
  },
  patch({ vacancyId, responseId, data }) {
    return cancellableRequest({
      method: 'patch',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}`,
      data,
    });
  },
  delete({ vacancyId, responseId }) {
    return cancellableRequest({
      method: 'delete',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}`,
    });
  },
};

export const VacanciesGuestsUsers = {
  get({ vacancyId, page }) {
    return cancellableRequest({
      method: 'get',
      url: `${apiUrlV1}/vacancies/${vacancyId}/guests`,
      params: {
        page,
      },
    });
  },

  syncLocation(params) {
    const mappedParams = mapSuitableUsersParams(params);
    const urlQuery = queryString.stringify(mappedParams, {
      arrayFormat: 'bracket',
    });

    window.history.replaceState(
      null,
      window.location.title,
      `${window.location.pathname}?${urlQuery}`,
    );
  },
};

export const VacanciesSuitableUsers = {
  get({ vacancyId, page, type, city }) {
    return cancellableRequest({
      method: 'get',
      url: `${apiUrlV1}/vacancies/${vacancyId}/suitable_users`,
      params: {
        page,
        type,
        city,
      },
    });
  },

  syncLocation(params) {
    const mappedParams = mapSuitableUsersParams(params);
    const urlQuery = queryString.stringify(mappedParams, {
      arrayFormat: 'bracket',
    });

    window.history.replaceState(
      null,
      window.location.title,
      `${window.location.pathname}?${urlQuery}`,
    );
  },
};

export const VacanciesReactionsResource = {
  post({ vacancyId, reactions }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/reactions`,
      params: {
        reactions,
      },
    });
  },
};

export const VacanciesAnalyticsResource = {
  get({ vacancyId, bannerPeriod, viewPeriod }) {
    return cancellableRequest({
      method: 'get',
      url: `${apiUrl}/vacancies/${vacancyId}/analytics`,
      params: {
        bannerPeriod,
        viewPeriod,
      },
    });
  },
};

/**
 * @typedef {'up'|'mark'|'banner'|'banner_pre290422'} ServiceName
 * @typedef {{
 *   up: number,
 *   mark: number,
 *   banner: number,
 * }} CartServices
 * @typedef {'add'|'remove'} ServiceAction
 */
export const VacanciesFavoritesResource = {
  post({ vacancyId }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/favorite`,
    });
  },
  delete({ vacancyId }) {
    return cancellableRequest({
      method: 'delete',
      url: `${apiUrl}/vacancies/${vacancyId}/favorite`,
    });
  },
};

export const VacanciesResponsesSourcesResource = {
  get({ vacancyId, responseId }) {
    return cancellableRequest({
      method: 'get',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}/sources`,
    });
  },
};

export const VacanciesResponsesFavouriteResource = {
  post({ vacancyId, responseId }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}/favorite`,
    });
  },
  delete({ vacancyId, responseId }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}/unfavorite`,
    });
  },
};

export const VacanciesResultResource = {
  post({ data }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${data.get('vacancyId')}}/result`,
      data,
    });
  },
};

export const VacanciesResponsesArchiveResource = {
  archive({ vacancyId, responseId }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}/archive`,
    });
  },
  unarchive({ vacancyId, responseId }) {
    return cancellableRequest({
      method: 'post',
      url: `${apiUrl}/vacancies/${vacancyId}/responses/${responseId}/unarchive`,
    });
  },
};

export const VacanciesAdminResource = {
  getVacancy({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}`;

    return cancellableRequest({
      method: 'get',
      url,
    });
  },
  getList(params) {
    const { companyId, ...rest } = params;
    let url = `${apiUrl}/companies/${companyId}/vacancies`;

    if (rest.vacancyListType) {
      url += `/${rest.vacancyListType}`;
      delete rest.vacancyListType;
    }

    return cancellableRequest({
      method: 'get',
      url,
      params: mapRequestParams(rest),
      paramsSerializer: VacanciesAdminResource.serializeParams,
    });
  },
  getMappedParams(params) {
    return mapLocationParams(params);
  },
  delete({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}`;

    return cancellableRequest({
      method: 'delete',
      url,
    });
  },
  unpublish({ companyId, id, result}) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/unpublish`;

    return cancellableRequest({
      method: 'post',
      url,
      params: { result },
    });
  },
  hide({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/draft`;

    return cancellableRequest({
      url,
      method: 'post',
    });
  },
  publish({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/publish`;

    return cancellableRequest({
      url,
      method: 'post',
    });
  },
  recover({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/recover`;

    return cancellableRequest({
      url,
      method: 'post',
    });
  },
  updateResult({ companyId, id, result }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/update_result`;

    return cancellableRequest({
      url,
      method: 'put',
      params: { result },
    });
  },
  getTabs({ companyId }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/tabs`;

    return cancellableRequest({
      url,
      method: 'get',
    });
  },
  /**
   *
   * @param {string} companyId
   * @param {string|number} id
   * @param {CartServices} services
   * @return {AxiosPromise}
   */
  setServices({ companyId, id, services }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/services`;

    return cancellableJsRequest({
      url,
      method: 'post',
      data: services,
    });
  },
  getServicePrice({ companyId, id, service }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/check_price`;

    return cancellableRequest({
      url,
      method: 'get',
      params: {
        service_type: service,
      },
    });
  },
  updateAutorenew({ companyId, id, enable }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/update_autorenew`;

    return cancellableRequest({
      url,
      method: 'put',
      params: {
        enable,
      },
    });
  },
  removeFromCart({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/remove_from_cart`;

    return cancellableRequest({
      url,
      method: 'post',
      params: {
        type: 'pay',
      },
    });
  },
  cancelModeration({ companyId, id }) {
    const url = `${apiUrl}/companies/${companyId}/vacancies/${id}/cancel_moderation`;

    return cancellableRequest({
      url,
      method: 'post',
    });
  },
  syncLocation(params) {
    const mappedParams = mapLocationParams(params);

    delete mappedParams.vacancyListType;
    delete mappedParams.companyId;

    const urlQuery = VacanciesAdminResource.serializeParams(mappedParams);

    window.history.replaceState(
      params,
      window.location.title,
      `${window.location.pathname}?${urlQuery}`,
    );
  },
  serializeParams(paramsObject) {
    const { filter } = paramsObject;
    const valueMappers = {
      paid_date_from: extractDate,
      paid_date_to: extractDate,
      created_date_from: extractDate,
      created_date_to: extractDate,
      paid_date_archived_from: extractDate,
      paid_date_archived_to: extractDate,
    };
    const cleanFilter = Object.keys(filter)
      .filter((key) =>
        Array.isArray(filter[key]) ? filter[key].length : !!filter[key],
      )
      .reduce((map, key) => {
        const value = filter[key];
        // eslint-disable-next-line no-param-reassign
        map[key] = valueMappers[key] ? valueMappers[key](value) : value;
        return map;
      }, {});

    return $.param({ ...paramsObject, filter: cleanFilter });
  },
};
